import MuiDialog, { type DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import type { FormEventHandler, ReactNode } from 'react';
import { BottomSheet } from './BottomSheet';
import { DialogLayout, type DialogAction, type DialogCancelAction } from './DialogLayout';

export type { DialogAction, DialogActionColor, DialogCancelAction } from './DialogLayout';

/** MUI's reasons plus the two ways the mobile bottom sheet can close itself. */
export type DialogCloseReason = 'backdropClick' | 'escapeKeyDown' | 'swipeDown' | 'closeButton';

export type DialogProps = {
  open: boolean;
  /** Also shows the X (desktop) and is the default for `cancelAction`. */
  onClose?: (event: object, reason: DialogCloseReason) => void;
  title?: ReactNode;
  /** The main action — contained, always last in the action row. */
  primaryAction?: DialogAction;
  /** Outlined, always first. `true` = "ביטול" that calls `onClose`. */
  cancelAction?: DialogCancelAction | true;
  /** Outlined, between cancel and the primary action. */
  secondaryActions?: DialogAction[];
  /** Wraps the dialog in a `<form>`; the primary action becomes its submit button. */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  /**
   * `none` — content runs edge to edge (grids, images).
   * `uniform` — the same padding on every side even without a title; the X floats over the corner.
   */
  contentPadding?: 'default' | 'none' | 'uniform';
  /** Desktop only — the mobile bottom sheet always spans the screen width. */
  maxWidth?: MuiDialogProps['maxWidth'];
  /** Desktop only. */
  fullWidth?: boolean;
  /** Desktop only — fixed tall height for grids / lists that fill the dialog. The mobile sheet always fits its content (up to 80%). */
  fullHeight?: boolean;
  /** Desktop only — lets absolutely positioned popups (e.g. `HebrewDatePicker`) spill outside the dialog. */
  allowOverflow?: boolean;
  disableEscapeKeyDown?: boolean;
  /** Lets focus leave the dialog — needed for content that opens its own popups (e.g. TinyMCE). */
  disableEnforceFocus?: boolean;
  keepMounted?: boolean;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  /** The dialog body. Title, X and action buttons come from the props above. */
  children?: ReactNode;
};

/**
 * Regular MUI dialog on desktop; below `md` renders a bottom sheet that opens at up to 80% of the
 * screen, expands to full screen on swipe up and closes on swipe down.
 */
export function Dialog({
  open,
  onClose,
  title,
  primaryAction,
  cancelAction,
  secondaryActions,
  onSubmit,
  contentPadding,
  maxWidth,
  fullWidth,
  fullHeight,
  allowOverflow,
  disableEscapeKeyDown,
  disableEnforceFocus,
  keepMounted,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  children
}: DialogProps) {
  const theme = useTheme();
  // noSsr: read the real value on first render, so a dialog opened on mount doesn't flash the desktop layout.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  const content = (
    <DialogLayout
      onClose={onClose}
      title={title}
      primaryAction={primaryAction}
      cancelAction={cancelAction}
      secondaryActions={secondaryActions}
      onSubmit={onSubmit}
      contentPadding={contentPadding}
    >
      {children}
    </DialogLayout>
  );

  const shared = {
    open,
    onClose,
    disableEscapeKeyDown,
    disableEnforceFocus,
    keepMounted,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy
  };

  if (isMobile) {
    return (
      <BottomSheet {...shared}>
        {content}
      </BottomSheet>
    );
  }

  return (
    <MuiDialog
      {...shared}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        className: ['SmDialog-paper', fullHeight && 'SmDialog-fullHeight', allowOverflow && 'SmDialog-allowOverflow']
          .filter(Boolean)
          .join(' ')
      }}
    >
      {content}
    </MuiDialog>
  );
}
